import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    ButtonGroup,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    Input,
    ListItemText,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
// filter display for different filter types

function areArraysShallowEqual(a, b) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

// text filter - type in value to filter column.
//
// The unconditional `setValue(filterValue)` sync this used to do dropped keystrokes: pushing a debounced
// filter kicks off an expensive re-filter, and the resulting `filterValue` echo landed after the user had
// already typed the next character, reverting the input to the older value. That was INC_35187 on the Truck
// ID column, fixed there by pointing that one column at a private copy of this component. Fixing the shared
// default instead so every column gets it.
function TextColumnFilter({ column }) {
    const { filterValue, setFilter } = column;
    const [value, setValue] = useState(filterValue || '');
    const setFilterRef = useRef(setFilter);
    const lastPushedRef = useRef(filterValue || '');
    const debounceTimerRef = useRef(null);
    const valueRef = useRef(value);
    valueRef.current = value;

    // react-table may recreate column instances; keep refs current without retriggering debounced work
    useEffect(() => {
        setFilterRef.current = setFilter;
    }, [setFilter]);

    // Apply incoming filterValue only when the input is clean (no uncommitted keystrokes).
    // Stale echoes from a slow table recalc must not overwrite what the user just typed.
    useEffect(() => {
        const incoming = filterValue || '';
        const local = valueRef.current;
        if (incoming === local) return;
        if (local !== lastPushedRef.current) return;
        setValue(incoming);
        lastPushedRef.current = incoming;
    }, [filterValue]);

    useEffect(() => {
        const clearDebounce = () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
        return clearDebounce;
    }, []);

    const handleChange = (event) => {
        const next = event.target.value;
        setValue(next);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        // debounce filtering; 50k rows re-filtering on every keystroke is brutal
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            const nextPushed = next || '';
            if (nextPushed === (lastPushedRef.current || '')) return;
            lastPushedRef.current = nextPushed;
            setFilterRef.current(next || undefined);
        }, 250);
    };

    return (
        <TextField
            disabled={column.hasOwnProperty('filterDisabled') && column.filterDisabled === true}
            id={`${column.Header.replaceAll(' ', '')}_filter`}
            onClick={(event) => event.stopPropagation()}
            style={{ width: '80%', marginLeft: '5px' }}
            value={value}
            onChange={handleChange}
        />
    );
}

// checkbox filter
function CheckboxFilter({ column }) {
    const { filterValue, setFilter } = column;
    const [value, setValue] = useState('all');

    useEffect(() => {
        setFilter(value);
    }, [value]);
    return (
        <div onClick={(event) => event.stopPropagation()}>
            <ButtonGroup style={{ padding: '0px 10px' }} size="small" aria-label="small outlined button group">
                <Button
                    id={`${column.Header.replaceAll(' ', '')}_all_filter`}
                    variant="contained"
                    color={value === 'all' ? 'primary' : 'default'}
                    onClick={(event) => {
                        event.stopPropagation();
                        setValue('all');
                    }}
                >
                    All
                </Button>
                <Button
                    id={`${column.Header.replaceAll(' ', '')}_true_filter`}
                    variant="contained"
                    color={value === true ? 'primary' : 'default'}
                    onClick={(event) => {
                        event.stopPropagation();
                        setValue(true);
                    }}
                >
                    T
                </Button>
                <Button
                    id={`${column.Header.replaceAll(' ', '')}_false_filter`}
                    variant="contained"
                    color={value === false ? 'primary' : 'default'}
                    onClick={(event) => {
                        event.stopPropagation();
                        setValue(false);
                    }}
                >
                    F
                </Button>
            </ButtonGroup>
        </div>
    );
}

function MultiSelectColumnFilter(obj) {
    const { column } = obj;
    const { filterValue, setFilter } = column;
    const [selected, setSelected] = useState(Array.isArray(filterValue) ? filterValue : []);
    const setFilterRef = useRef(setFilter);
    const filterValueRef = useRef(Array.isArray(filterValue) ? filterValue : []);

    useEffect(() => {
        setFilterRef.current = setFilter;
    }, [setFilter]);

    useEffect(() => {
        // Track the latest external filter value without creating a dependency loop.
        filterValueRef.current = Array.isArray(filterValue) ? filterValue : [];
    }, [filterValue]);

    useEffect(() => {
        // Keep local UI state in sync with external filter resets (e.g. "Clear Filters").
        // When filters are cleared, react-table may set filterValue to undefined; we should reset to [].
        const nextSelected = Array.isArray(filterValue) ? filterValue : [];
        setSelected((prev) => (areArraysShallowEqual(prev, nextSelected) ? prev : nextSelected));
    }, [filterValue]);

    useEffect(() => {
        // Only push changes *from this component's UI*.
        // Do NOT depend on filterValue here; otherwise external updates (e.g. Clear Filters)
        // can race and get overwritten by a stale `selected`.
        // Also skip no-op updates (including initial mount) to avoid an expensive table recalculation.
        if (areArraysShallowEqual(selected, filterValueRef.current)) return;
        setFilterRef.current(selected.length ? selected : undefined);
    }, [selected]);

    const accessor = obj.column.id;
    const opts = useMemo(() => {
        // Prefer react-table's preFilteredRows if available; otherwise fall back to raw data
        const preFilteredRows = column.preFilteredRows || obj.preFilteredRows;
        if (Array.isArray(preFilteredRows)) {
            return [...new Set(preFilteredRows.map((row) => row.values?.[accessor]))].filter((v) => v != null);
        }
        if (Array.isArray(obj.data)) {
            return [...new Set(obj.data.map((item) => item?.[accessor]))].filter((v) => v != null);
        }
        return [];
    }, [accessor, column.preFilteredRows, obj.preFilteredRows, obj.data]);

    const selectOptions = useMemo(() => {
        let options = [];
        if (Array.isArray(column?.editProps?.options)) {
            // match with option values
            opts.forEach((opt) => {
                const optionFound = column.editProps.options.find((item) => item.value === opt);
                if (optionFound) {
                    options.push(optionFound);
                } else {
                    options.push({ text: opt, value: opt });
                }
            });
        } else {
            options = opts.map((opt) => ({ text: opt, value: opt }));
        }
        options.sort((a, b) => String(a.text).localeCompare(String(b.text)));
        return options;
    }, [column?.editProps?.options, opts]);

    return (
        <Select
            onClick={(event) => event.stopPropagation()}
            multiple
            value={selected}
            size="small"
            style={{ width: 150 }}
            renderValue={(selected) => <span style={{ fontSize: '10px' }}>{selected.join(', ')}</span>}
            onChange={(event, value) => {
                setSelected(event.target.value);
            }}
            input={<Input />}
        >
            {selectOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                    <Checkbox checked={selected.includes(item.value)} />
                    <ListItemText primary={item.text} />
                </MenuItem>
            ))}
        </Select>
    );
}

// select filter - select value from all options from the column
function SelectColumnFilter(obj) {
    const { column } = obj;
    const { filterValue, setFilter } = column;

    const accessor = obj.column.id;
    const opts = useMemo(() => {
        const preFilteredRows = column.preFilteredRows || obj.preFilteredRows;
        if (Array.isArray(preFilteredRows)) {
            return [...new Set(preFilteredRows.map((row) => row.values?.[accessor]))].filter((v) => v != null);
        }
        if (Array.isArray(obj.data)) {
            return [...new Set(obj.data.map((item) => item?.[accessor]))].filter((v) => v != null);
        }
        return [];
    }, [accessor, column.preFilteredRows, obj.preFilteredRows, obj.data]);

    const selectOptions = useMemo(() => {
        let options = [];
        if (Array.isArray(column?.editProps?.options)) {
            // match with option values
            opts.forEach((opt) => {
                const optionFound = column.editProps.options.find((item) => item.value === opt);
                if (optionFound) {
                    options.push(optionFound);
                } else {
                    options.push({ text: opt, value: opt });
                }
            });
        } else {
            options = opts.map((opt) => ({ text: opt, value: opt }));
        }
        options.sort((a, b) => String(a.text).localeCompare(String(b.text)));
        return options;
    }, [column?.editProps?.options, opts]);

    return (
        <Select
            onClick={(event) => event.stopPropagation()}
            value={filterValue || ''}
            style={{ width: '80%', marginLeft: '5px' }}
            onChange={(e) => {
                setFilter(e.target.value || '');
            }}
            native
        >
            <option />
            {selectOptions.map((item) => (
                <option key={item.value} value={item.value}>
                    {item.text}
                </option>
            ))}
        </Select>
    );
}

function DateFilter({ column }) {
    const { filterValue, setFilter } = column;
    const [open, setOpen] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [date1, setDate1] = useState('');
    const [date2, setDate2] = useState('');
    const [canSubmit, setCanSubmit] = useState(false);

    useEffect(() => {
        if (filterType === 'all') {
            setCanSubmit(true);
            return;
        }
        if (filterType === 'between') {
            if (!date1 || !date2) {
                setCanSubmit(false);
                return;
            }
        } else if (!date1) {
            setCanSubmit(false);
            return;
        }

        setCanSubmit(true);
    }, [filterType, date1, date2]);

    return (
        <>
            <Grid container>
                <Grid item xs={10}>
                    <Button
                        id={`${column.Header.replaceAll(' ', '')}_date_filter`}
                        color={filterType === 'all' ? 'default' : 'primary'}
                        variant={filterType === 'all' ? 'text' : 'contained'}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(true);
                        }}
                    >
                        Filter
                    </Button>
                </Grid>
            </Grid>
            <Dialog maxWidth="sm" fullWidth open={open} onClick={(event) => event.stopPropagation()}>
                <DialogTitle>Filter Date Column: {column.Header}</DialogTitle>
                <DialogContent onClick={(event) => event.stopPropagation()}>
                    <Grid container onClick={(event) => event.stopPropagation()}>
                        <Grid item xs={12}>
                            <Select
                                id="DateFilterSelect"
                                onClick={(event) => event.stopPropagation()}
                                value={filterType}
                                style={{ width: '100%', margin: '5px' }}
                                onChange={(e) => {
                                    setFilterType(e.target.value);
                                }}
                                native
                            >
                                <option value="all">All</option>
                                <option value="equals">Equals</option>
                                <option value="greaterThan">After</option>
                                <option value="lessThan">Before</option>
                                <option value="between">Between (Inclusive)</option>
                            </Select>
                        </Grid>
                        {filterType !== 'all' && (
                            <Grid item xs={12}>
                                <TextField
                                    id="DateField_1"
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(e) => {
                                        setDate1(e.target.value);
                                    }}
                                    style={{ width: '100%', margin: '5px' }}
                                    type="date"
                                    value={date1}
                                />
                            </Grid>
                        )}
                        {filterType === 'between' && (
                            <>
                                <Grid item xs={12}>
                                    <div style={{ textAlign: 'center' }}>AND</div>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="DateField_2"
                                        onChange={(e) => {
                                            setDate2(e.target.value);
                                        }}
                                        onClick={(event) => event.stopPropagation()}
                                        style={{ width: '100%', margin: '5px' }}
                                        type="date"
                                        value={date2}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <Button
                                id="OK"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setFilter({
                                        filterType,
                                        date1,
                                        date2,
                                    });
                                    setOpen(false);
                                }}
                                disabled={!canSubmit}
                            >
                                OK
                            </Button>
                            <Button
                                onClick={(event) => {
                                    setOpen(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
}

export { TextColumnFilter, SelectColumnFilter, CheckboxFilter, DateFilter, MultiSelectColumnFilter };
